/** Display-only preview chips for recognized quick-add tokens (# @ + >). */
import { Box, Chip } from '@mui/material';
import TagChip from '@/components/ui/TagChip';
import { contextChipSx } from '@/components/ui/taskVisualTokens';
import { formatTaskDate } from '@/features/tasks/utils/views/taskTimeUtils';

const previewDenseSx = {
  height: 16,
  fontSize: '0.65rem',
  '& .MuiChip-label': { px: 0.75 },
};

/**
 * @param {Partial<import('@/types').NewTask>} parsed
 */
function hasParsedTokenPreview(parsed) {
  return (
    (parsed.projects?.length ?? 0) > 0 ||
    (parsed.contexts?.length ?? 0) > 0 ||
    !!parsed.person ||
    !!parsed.date
  );
}

/**
 * @param {{
 *   parsed: Partial<import('@/types').NewTask>,
 *   dense?: boolean,
 * }} props
 */
export function TaskInputTokenPreview({ parsed, dense = false }) {
  if (!hasParsedTokenPreview(parsed)) {
    return null;
  }

  const dateLabel = parsed.date
    ? `>${formatTaskDate(parsed.date) ?? parsed.date}`
    : null;

  return (
    <Box
      data-testid="task-input-token-preview"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        alignItems: 'center',
        mt: 0.5,
        px: 0.25,
      }}
    >
      {(parsed.projects ?? []).map((project) => (
        <TagChip
          key={`preview-p-${project}`}
          tag={project}
          dense={dense}
          data-testid={`task-input-preview-project-${project}`}
        />
      ))}
      {(parsed.contexts ?? []).map((context) => (
        <TagChip
          key={`preview-c-${context}`}
          tag={`@${context}`}
          dense={dense}
          data-testid={`task-input-preview-context-${context}`}
        />
      ))}
      {parsed.person ? (
        <TagChip
          key="preview-person"
          tag={parsed.person}
          label={`+${parsed.person}`}
          dense={dense}
          data-testid="task-input-preview-person"
        />
      ) : null}
      {dateLabel ? (
        <Chip
          label={dateLabel}
          size="small"
          variant="outlined"
          data-testid="task-input-preview-date"
          sx={{
            ...contextChipSx,
            ...(dense ? previewDenseSx : {}),
          }}
        />
      ) : null}
    </Box>
  );
}
